import { useEffect, useState } from 'react';
import { Card, GenericTable } from '@cdaas/reusable-components-daas';
import { AdminProduct, Country } from '@cdaas/api-clients/dist/product-service-openapi';
import { useTranslation } from 'react-i18next';

import {
  productServiceCountriesClient,
  productServiceMaterialsClient,
  productServiceVariantsClient,
  productServiceProductClient,
} from '../../../../api-client/product-service-client';
import PageTemplate, { PageTemplateTranslations } from '../PageTemplate/PageTemplate';

interface MaterialWithCountry {
  code: string;
  variantId: number;
  country: Country;
  product?: AdminProduct;
}

export default function AvailabilityPage() {
  const [materials, setMaterials] = useState<MaterialWithCountry[]>([]);
  const [materialsLoading, setMaterialsLoading] = useState<boolean>(false);
  const [materialsApiError, setMaterialsApiError] = useState<boolean>(false);

  const [productLoading, setProductLoading] = useState<boolean>(false);

  const { t } = useTranslation('backOffice');
  const pageTemplateTranslations: PageTemplateTranslations = {
    title: t('availabilityPage.title'),
  };

  const fetchMaterialsWithNoAvailability = async () => {
    try {
      setMaterialsLoading(true);
      const promises = await Promise.all([
        productServiceMaterialsClient.getMaterialsWithoutAvailability().then(res => res.data.materials),
        productServiceCountriesClient.getCountries().then(res => res.data.countries),
      ]);

      const variantIds = new Set<number>(promises[0].map(material => material.variant_id));
      const variants = await Promise.all(Array.from(variantIds).map(id => productServiceVariantsClient.getAdminVariant(id).then(res => res.data!)));
      const products = await Promise.all(
        variants.map(variant => productServiceProductClient.getAdminProduct(variant.product_id!).then(res => res.data.product)),
      );

      const materials: MaterialWithCountry[] = promises[0].map(material => ({
        code: material.code,
        variantId: material.variant_id,
        country: promises[1].find(country => country.id === material.country_id)!,
        product: products.find(product => product.id === variants.find(variant => variant.id === material.variant_id)?.product_id),
      }));

      setMaterials(materials);
    } catch {
      setMaterialsApiError(true);
    }
    setMaterialsLoading(false);
  };

  const redirectToProduct = async (material: MaterialWithCountry) => {
    setProductLoading(true);
    if (material.product?.id) {
      window.location.href = `${process.env.REACT_APP_PRODUCT_ADMIN_URL}/product/${material.product.id}/main/${material.country.code}`;
      // Browser can remember earlier state fully when going to next other url page
      // This can happen at least in Chrome
      // With setTimeout we enable show button on background after redirect
      setTimeout(() => {
        setProductLoading(false);
      }, 4000);
    } else {
      alert(`Not found product ID for variant ID ${material.variantId}. Please investigate why.`);
      setProductLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterialsWithNoAvailability();
  }, []);

  return (
    <PageTemplate
      translations={pageTemplateTranslations}
      pageContent={
        <Card
          title={t('availabilityPage.cardTitle')}
          wrapperDivCustomProps={{ backgroundColor: 'white', padding: { size: 'lg' }, borderRadius: { size: 'md' } }}
          tag={
            materials?.length
              ? { text: t('availabilityPage.tagCheck') }
              : materialsLoading
                ? { text: t('availabilityPage.tagLoading'), tagProps: { backgroundColor: 'orange400' } }
                : undefined
          }
          isInitiallyOpen={true}
        >
          <GenericTable
            isGenericTableInsideCard={true}
            noDataText={t('availabilityPage.table.noDataText')}
            errorText={materialsApiError ? t('availabilityPage.table.errorText') : undefined}
            loading={materialsLoading ? 'long' : 'none'}
            inputData={materials}
            rowConfigs={[
              {
                header: t('availabilityPage.table.headerCode'),
                key: 'code',
                sortFunction: (a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0),
                cellWidthMultiplier: 1,
              },
              {
                header: t('availabilityPage.table.headerProductName'),
                formatValue: (_value, row) => row.product?.name ?? t('availabilityPage.table.errorWithProduct'),
                sortFunction: (a, b) => {
                  if (!a.product || !b.product) return 0;
                  else if (a.product.name < b.product.name) return -1;
                  else if (a.product.name > b.product.name) return 1;
                  else return 0;
                },
                cellWidthMultiplier: 2,
              },
              {
                header: t('availabilityPage.table.headerCountry'),
                key: 'country.name',
                sortFunction: (a, b) => (a.country.name < b.country.name ? -1 : a.country.name > b.country.name ? 1 : 0),
                cellWidthMultiplier: 1,
              },
            ]}
            buttonConfig={{
              name: t('availabilityPage.table.rowButtonText'),
              buttonProps: {
                iconName: 'chevron-right',
                size: 'md',
                iconOnRight: true,
                disabled: productLoading,
              },
              buttonClickCallback: redirectToProduct,
            }}
          />
        </Card>
      }
    />
  );
}
